<style scoped>
iframe {
  height: 1000px;
  width: 1600px;
}
</style>
<template>
  <div class="container my-5">
    <div class="row mb-3">
      <div class="col d-flex align-items-center">
        <h2 class="mb-0">Stage Overview</h2>
      </div>
      <div class="col-auto">
        <button type="button" class="btn btn-light" v-c-modal:editDrawer.parent="{}">
          <i class="fal fa-cog"></i> Stage Settings
        </button>
      </div>
    </div>

    <div class="row">
      <div class="col-md-6 col-xl-3 mb-4">
        <div class="card card-widget">
          <div class="card-body">
            <h3 class="widget-title">Time Spent</h3>
            <div class="row">
              <div class="col-6">
                <p class="text-muted small">Spent</p>
                <p class="lead">{{ stage.spent_hours }} hrs</p>
              </div>
              <div class="col-6">
                <p class="text-muted small">Budget</p>
                <p class="lead">{{ stage.budget_hours }} hrs</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-6 col-xl-3 mb-4">
        <div class="card card-widget">
          <div class="card-body">
            <h3 class="widget-title">Days Remaining</h3>
            <div v-if="daysUntil >= 0" class="row">
              <div class="col-6">
                <p class="text-muted small">Remaining</p>
                <p class="lead">{{ daysRemaining > -1 ? daysRemaining : 0 }} days</p>
              </div>
              <div class="col-6">
                <p class="text-muted small">Dates</p>
                <p class="small">
                  {{ stage.commenced | moment('DD/MM/YYYY') }} -<br/>
                  {{ stage.completed | moment('DD/MM/YYYY') }}
                </p>
              </div>
            </div>
            <div v-else class="row">
              <div class="col">
                <p class="text-muted small">Not in scheduled time yet</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-6 col-xl-3 mb-4">
        <div class="card card-widget">
          <div class="card-body">
            <h3 class="widget-title">Estimates</h3>
            <div class="row">
              <div class="col-6">
                <p class="text-muted small">Accuracy</p>
                <p class="lead">{{ stage.estimate_v_actual }}%</p>
              </div>
              <div class="col-6">
                <p class="text-muted small">Remaining</p>
                <p class="lead">{{ stage.remaining_estimate }} hrs</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-6 col-xl-3 mb-4">
        <div class="card card-widget">
          <div class="card-body">
            <h3 class="widget-title">Additional Time</h3>
            <div class="row">
              <div class="col-6">
                <p class="text-muted small">Unplanned</p>
                <p class="lead">{{ stage.unplanned_hours }} hrs</p>
              </div>
              <div class="col-6">
                <p class="text-muted small">Warranty</p>
                <p class="lead">{{ stage.warranty_time_spent }} hrs</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row display-flex">
      <div class="col-lg-6 mb-4">
        <div class="card h-100">
          <div class="card-body">
            <div class="row mb-3">
              <div class="col d-flex align-items-center">
                <h2 class="card-title text-truncate mb-0">Team Members</h2>
              </div>
              <div class="col-auto">
                <button type="button" class="btn btn-sm btn-link" v-c-modal:editDrawer.parent="{}">
                  <i class="far fa-pen"></i>
                </button>
              </div>
            </div>

            <ul class="project-members project-members-lg">
              <li v-if="stage.lead" class="lead">
                <i class="fal fa-crown"></i>
                <img :src="getAvatar(stage.lead.id)" :title="'Lead: ' + stage.lead.name" />
              </li>
              <li v-for="member in stage.members" :key="member.id">
                <img :src="getAvatar(member.id)" :title="member.name" />
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div v-if="stage.opportunity || stage.budget" class="col-lg-6 mb-4">
        <div class="card h-100">
          <div class="card-body">
            <h2 class="card-title mb-3">Linked</h2>
            <div class="row">
              <div v-if="stage.opportunity" class="col-md-6">
                <p>
                  <span class="data-label">Opportunity</span>
                  <router-link
                    v-if="stage.opportunity.id"
                    target="_blank"
                    tag="a"
                    :to="{
                      name: 'opportunities.overview',
                      params: { opportunity_id: stage.opportunity.id },
                    }"
                  >
                    {{ stage.opportunity.formatted_name }}
                  </router-link>
                </p>
              </div>
              <div v-if="stage.budget" class="col-md-6">
                <p>
                  <span class="data-label">T&M Budget</span>
                  <router-link
                    target="_blank"
                    tag="a"
                    :to="{
                      name: 'budgets.overview',
                      params: { budget_id: stage.budget.id },
                    }"
                  >
                    {{ stage.budget.name }}
                  </router-link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-4">
      <div class="card-body">
        <div class="row mb-3">
          <div class="col d-flex align-items-center">
            <h2 class="card-title mb-0">Burndown</h2>
          </div>
          <div class="col-12 col-md-4">
            <multiselect :show-labels="false" :options="sprints" v-model="sprintSelected" label="title" track-by="id" placeholder="Select Sprint"></multiselect>
          </div>
        </div>
        <apex-chart id="chart-hourly-usage" type="line" :series="series" :options="graphOptions" height="350"/>
      </div>
    </div>

    <template v-if="sprints && showSprint()">
      <div class="row mt-5">
        <div class="col d-flex align-items-center">
          <h2 class="mb-0">Sprints</h2>
        </div>
        <div class="col-auto">
          <button type="button" class="btn btn-primary" @click="openSprintDraw({})">
            <i class="far fa-plus"></i> Sprint
          </button>
        </div>
      </div>

      <ul class="list-data has-button">
        <li class="data-heading d-none d-lg-block">
          <div class="row">
            <div class="col-lg-3"><p>Title</p></div>
            <div class="col-lg-3"><p>Dates</p></div>
            <div class="col-lg-2"><p>Status</p></div>
            <div class="col-lg-2"><p>Velocity</p></div>
            <div class="col-lg-2"><p>Effort</p></div>
          </div>
        </li>
        <li v-bind:key="sprint.id" v-for="sprint in sprints">
          <router-link :to="{name: 'projects.stage3.scrum', params: {user_story_doc_id: stage.scrum_user_story_doc_id, sprint_id: sprint.id}}">
            <div class="row">
              <div class="col-12 col-lg-3">
                <p class="strong">{{ sprint.title }}</p>
              </div>
              <div class="col-6 col-lg-3">
                <p>
                  <span class="data-label d-lg-none">Dates:</span>
                  {{ sprint.start_date | moment('DD/MM/YYYY') }} -
                  <span>{{ sprint.end_date | moment('DD/MM/YYYY') }}</span>
                </p>
              </div>
              <div class="col-6 col-lg-2">
                <span class="status" :class="classForStatus(sprint.status)">
                  {{ sprint.status }}
                </span>
              </div>
              <div class="col-6 col-lg-2">
                <p>
                  <span class="data-label d-lg-none">Velocity:</span>
                  {{ sprint.velocity }}
                </p>
              </div>
              <div class="col-6 col-lg-2">
                <ul class="details">
                  <li v-for="(status, effort) in sprint.statuses" :key="effort">{{status}} - {{effort}}</li>
                </ul>
              </div>
            </div>
          </router-link>
          <button type="button" class="btn" @click.prevent="openSprintDraw(sprint)">
            <i class="fal fa-pencil"></i>
          </button>
        </li>
      </ul>
    </template>

    <h2 class="mt-5">Opportunities</h2>
    <ul v-if="stage.next_opportunities" class="list-data">
      <li v-bind:key="opportunity.id" v-for="opportunity in stage.next_opportunities">
        <router-link
          tag="a"
          :to="{ name: 'opportunities.overview', params: { 'opportunity_id': opportunity.id }}"
        >
          <div class="row">
            <div class="col-12 col-lg-6">
              <p class="strong">{{ opportunity.name }}</p>
            </div>
            <div class="col-6 col-lg-4">
              <p>
                <span class="data-label">Lead Type:</span>
                {{ opportunity.lead_type }}
              </p>
            </div>
            <div class="col-6 col-lg-2 justify-content-end">
              <span class="status status-primary">{{ opportunity.status }}</span>
            </div>
          </div>
        </router-link>
      </li>
    </ul>

    <h2 class="mt-5">Signoffs</h2>
    <ul v-if="stage.signoffs" class="list-data">
      <li v-bind:key="signoff.id" v-for="signoff in stage.signoffs">
        <router-link
          tag="a"
          :to="{
            name: 'signoffs.overview',
            params: { signoff_id: signoff.id },
          }"
        >
          <div class="row">
            <div class="col-12 col-lg-6">
              <p class="strong">{{ signoff.name }}</p>
            </div>
            <div class="col-6 col-lg-4">
              <p>
                <span class="data-label">Signed On:</span>
                {{ signoff.sign_request.signed_on ? signoff.sign_request.signed_on : 'Not Sent' }}
              </p>
            </div>
            <div class="col-6 col-lg-2 justify-content-end">
              <span class="status status-primary">{{ signoff.status }}</span>
            </div>
          </div>
        </router-link>
      </li>
    </ul>

    <context-files
      type="projects"
      :id="$route.params.project_id"
      title="Project Files"
    ></context-files>

    <h2 class="mt-5">Evaluation / Warranty Tickets</h2>
    <!-- The stage layout scrolls inside .main-content rather than #page-content-wrapper -->
    <c-data-list ref="dataList" :items="stage.tickets" scrollTarget=".main-content">
      <template v-slot:columns>
        <div class="col-1"></div>
        <div class="col-1">Ticket #</div>
        <div class="col-7">Subject</div>
        <div class="col-2">Created</div>
        <div class="col-1">Hrs</div>
      </template>
      <template v-slot:default="slotProps">
        <a>
          <div class="row">
            <div class="col-1">
              <c-avatar
                small
                :name="slotProps.item.user ? slotProps.item.user.name : ''"
                :url="getAvatar(slotProps.item.user_id)"
              />
            </div>
            <div class="col-1">
              <p class="ticket-id">
                <a
                  target="_blank"
                  v-bind:href="'http://support.codium.com.au/helpdesk/tickets/' + slotProps.item.freshdesk_ticket_id"
                >
                  #{{ slotProps.item.freshdesk_ticket_id }}
                </a>
              </p>
            </div>
            <div class="col-7">
              <p :title="slotProps.item.subject" class="text-truncate">
                {{ slotProps.item.subject }}
              </p>
            </div>
            <div class="col-2">
              <p class="text-truncate">
                {{ slotProps.item.freshdesk_created_at | moment('DD/MM/YYYY') }}
              </p>
            </div>
            <div class="col-1">
              <p class="text-truncate">{{ slotProps.item.time_entries_total }} hrs</p>
            </div>
          </div>
          <!-- /.row -->
        </a>
      </template>
    </c-data-list>

    <sprint-drawer ref="sprintDrawer" :edit-sprint="editSprint" />
  </div>
</template>

<script>
//http://stackoverflow.com/documentation/javascript/4742/same-origin-policy-cross-origin-communication/30128/safe-cross-origin-communication-with-messages#t=201705171403471400655
//<div id="iframe-wrapper" style="position: absolute; width: 100%; height: 100%; top: 0px; background-color: #c3c3c3; z-index: 1000; opacity: 0.4;"><i class="fa fa-sticky-note" style=" font-size: 24px; z-index: 1005; position: absolute; top: 100px; left: 100px; color: red;"></i></div>
import { notify, http } from 'utils'
import ApexChart from 'vue-apexcharts'
import { mapState, mapActions } from 'vuex'
import Datepicker from 'vuejs-datepicker'
import { Selectize, LaddaButton } from 'components/form/input'
import SprintDrawer from './SprintDrawer'
import ContextFiles from 'components/ai/ContextFiles'

export default {
  components: {
    LaddaButton,
    Datepicker,
    Selectize,
    SprintDrawer,
    ContextFiles,
    ApexChart
  },

  data: function () {
    return {
      edit: false,
      loading: false,
      users: [],
      editSprint: {},
      burndown: {
        ideal: [],
        remaining_effort: [],
        days: []
      },
      sprintSelected: null
    }
  },

  mounted() {
    //clear the sprints if they exists from a previously loaded user story doc
    this.clearSprints()

    // only make request if user story doc exists
    // it might not in the case of a full budget on stage 3 until it is converted
    if (this.userStoryDocumentForType) {
      this.getUserStoryDoc(this.userStoryDocumentForType)
        .then((response) => {
          this.getSprints().then(() => {
            this.getStageBurnDown(this.stage.id)
          })
        })
        .catch((error) => {})

      
    }
  },

  watch: {
      sprintSelected (sprint, old) {
        this.getSprintBurnDown(sprint.id)
      }
    },

  computed: {
    ...mapState('yodizproject', {
      project: 'single',
    }),
    ...mapState('yodizproject/stages', {
      stage: 'single',
    }),
    ...mapState('yodizproject/sprints', {
      sprint: 'single',
      sprints: 'list',
    }),
    series () {
      return [{
        name: 'ideal_remaining',
        data: this.burndown['ideal']
      },
      {
        name: 'remaining_effort',
        data: this.burndown['remaining_effort']
      }]
    },
    graphOptions () {
      return {
        colors: [
          '#20A1B6',
        ],
        chart: {
          toolbar: {
            show: false
          },
          zoom: {
            enabled: false
          },
        },
        legend: {
          position: 'top',
          horizontalAlign: 'right',
        },
        dataLabels: {
          enabled: false
        },
        xaxis: {
          type: 'category',
          categories: this.burndown['days'],
        },
        yaxis: {
          tickAmount: 10,
        },
        stroke: {
          width: [5, 7, 5],
          dashArray: [5, 0, 0]
        },
        grid: {
          strokeDashArray: 4
        },
        tooltip: {
          enabled: true,
          shared: true,
          followCursor: true,
          intersect: false,
          theme: 'dark',
          x: {
            show: true,
          }
        },
        annotations: {
          xaxis: [{
            x: "Tue 02 Jan",
            strokeDashArray: 0,
            borderColor: '#775DD0',
            label: {
              borderColor: '#775DD0',
              style: {
                color: '#fff',
                background: '#775DD0',
              },
              text: 'Anno Test',
            }
          }, {
            x: "Wed 03 Jan",
            x2: "Wed 10 Jan",
            fillColor: '#B3F7CA',
            opacity: 0.4,
            label: {
              borderColor: '#B3F7CA',
              style: {
                fontSize: '10px',
                color: '#fff',
                background: '#00E396',
              },
              offsetY: -10,
              text: 'X-axis range',
            }
          }]
        }
      }
    },
    api: function () {
      return process.env.VUE_APP_API_URL
    },
    userStoryDocumentForType() {
      //For stage 1 / 2 projects use the deliberables sprints, for stage 3 we use the scrumboard
      if (this.stage.stage.id == 3) {
        return this.stage.scrum_user_story_doc_id
      } else {
        return this.stage.user_story_doc_id
      }
    },
    statusClass() {
      switch (this.sprint.status) {
        case 'Complete':
          return { 'status-primary': true }
          break
        case 'Scheduled':
          return { 'status-info': true }
          break
        case 'Active':
          return { 'status-success': true }
          break
      }
    },
    archived: {
      get: function () {
        return this.project.archived_at ? 1 : 0
      },
      set: function (value) {
        if (value == 0) {
          this.project.archived_at = null
        } else {
          this.project.archived_at = moment().format('YYYY-MM-DD')
        }
      },
    },
    percentBudgetUsed() {
      if (this.stage.budget_hours > 0) {
        return (this.stage.spent_hours / this.stage.budget_hours) * 100
      }

      return 0
    },
    percentDaysRemaining() {
      if (this.daysBudget > 0) {
        return (this.daysRemaining / this.daysBudget) * 100
      }
      return 0
    },
    daysBudget() {
      var startDate = moment(this.stage.commenced)
      var endDate = moment(this.stage.completed)

      return Math.max(endDate.diff(startDate, 'days'), 0)
    },
    daysUntil() {
      var today = moment()
      var endDate = moment(this.stage.commenced)

      return today.diff(endDate, 'days')
    },
    daysRemaining() {
      var today = moment()
      var endDate = moment(this.stage.completed)

      return endDate.diff(today, 'days')
    },
    usagePercentageChartData() {
      return {
        datasets: [
          {
            data: [this.percentBudgetUsed, 100 - Math.min(this.percentBudgetUsed, 100)],
            backgroundColor: ['#0074CB', '#CFCFCF'],
            borderWidth: 0,
          },
        ],
      }
    },

    daysRemainingChartData() {
      return {
        datasets: [
          {
            data: [
              this.percentDaysRemaining < 0 ? 0 : this.percentDaysRemaining,
              100 - Math.min(this.percentDaysRemaining, 100),
            ],
            backgroundColor: ['#0074CB', '#CFCFCF'],
            borderWidth: 0,
          },
        ],
      }
    },
  },
  methods: {
    ...mapActions('yodizproject/sprints', {
      getSprints: 'getList',
      getSprint: 'getSingle',
      setSprint: 'setSingleFromList',
      clearSprints: 'clearData',
    }),
    ...mapActions('yodizuserstorydoc', {
      getUserStoryDoc: 'getSingle',
    }),
    showSprint() {
      return this.$route.name == 'projects.stage3.overview'
    },
    getSprintBurnDown(sprintId) {
      http.get(`/userstorydocs/${this.userStoryDocumentForType}/sprints/${sprintId}/burndown`).then(response => {
        this.burndown = response.data
      });
    },
    getStageBurnDown() {
      console.log(this.project)
      http.get(`/projects/${this.project.id}/stages/${this.stage.id}/burndown`).then(response => {
        this.burndown = response.data
      });
    },
    classForStatus(status) {
      switch (status) {
        case 'Complete':
          return { 'status-primary': true }
          break
        case 'Scheduled':
          return { 'status-info': true }
          break
        case 'Active':
          return { 'status-success': true }
          break
      }
    },
    saveProject: function () {
      var that = this
      this.loading = true

      return client({
        path: '/projects/' + this.project.id,
        entity: this.project,
        method: 'PUT',
      }).then(
        function (response) {
          that.$emit('update:project', response.entity)
          that.edit = false
        },
        function (response) {
          if (response.status.code == 422) {
            var firstValidationIssue = response.entity[Object.keys(response.entity)[0]]
          } else {
          }
        }
      )

      this.edit = false
    },
    confirmDelete(projectId) {
      var that = this

      this.$swal({
        title: 'Confirm Delete',
        html: 'Are you sure you want to delete this project',
        type: 'question',
        showCancelButton: true,
        confirmButtonText: 'Delete',
      }).then(
        function () {
          that.deleteProject(projectId)
        },
        function () {}
      )
    },

    deleteProject(projectId) {
      var that = this

      return client({ path: '/projects/' + projectId, method: 'DELETE' }).then(
        function (response) {
          that.$router.push({ name: 'projects.index' })
        },
        function (response) {
          if (response.status.code == 422) {
            var firstValidationIssue = response.entity[Object.keys(response.entity)[0]]

            notify.error(firstValidationIssue)
          } else {
            notify.error('Error deleting project')
          }
        }
      )
    },
    getAvatar: function (id) {
      return process.env.VUE_APP_API_URL + '/user/' + id + '/avatar'
    },
    openSprintDraw(sprint) {
      this.editSprint = sprint

      this.$nextTick(() => {
        this.$refs.sprintDrawer.open()
      })
    },
  },
}
</script>
